import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Column } from 'primereact/column';
import { useSelector } from 'react-redux';
import { Container } from './styles';

import AuthService from '../../services/AuthService';
import LicencaService from '../../services/LicencaService';
import history from '../../services/history';
import {
  getPageParams,
  errorHandle,
  padLeft,
  calcNaxItemsPage,
  isScreenMobile,
} from '../../util/functions';

import BotaoMenuGrid from '../../components/BotaoMenuGrid';
import ButtonSp from '../../components/ButtonSp';
import DropdownSp from '../../components/DropdownSp';
import LabelSp from '../../components/LabelSp';
import MessageInputText from '../../components/MessageInputText';

import LicencaCrud from './crud';
import DialogoChave from './dialogoChave';
import { StateScreen } from '../constants';
import CrudHeader from '../../components/CrudHeader';
import DataTableSp from '../../components/DataTableSp';
import { NOMES_SITUACAO, OPCOES_SITUACAO, formataDataHora } from './util';

// Licenciamento por assento (sistemas 10.9+): chaves de instalacao geradas no portal e
// o servidor do SpeedyAgent que usou cada uma. Os legados (< 10.9) seguem nas telas de
// chave. O perfil Revenda so ve os clientes da propria revenda (filtro na API).
export default function Licenca(props) {
  const filterVisible = useSelector(state => state.global.filterVisible);

  const pageParams = useMemo(() => getPageParams(props.match.params, props.location.search), [
    props.location.search,
    props.match.params,
  ]);

  const filterService = useMemo(() => LicencaService.getFilter(), []);

  const toBack = pageParams.toBack || '/licencas';

  const [filter, setFilter] = useState(filterService);
  const [licencas, setLicencas] = useState([]);

  const [pageLimit, setPageLimit] = useState(filterService.limit);
  const [first, setFirst] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);

  const [pedindoContrato, setPedindoContrato] = useState(false);
  const [chaveGerada, setChaveGerada] = useState(null);

  const calcLimit = useCallback(() => {
    let ret;
    if (filterVisible) {
      ret = calcNaxItemsPage(10, 14, 19);
    } else {
      ret = calcNaxItemsPage(13, 17, 22);
    }
    setPageLimit(ret);
    return ret;
  }, [filterVisible]);

  const handleBuscar = useCallback(
    async (_filter, _page, resetPage = true) => {
      _filter.page = _page || 0;
      _filter.limit = calcLimit();
      try {
        const result = await LicencaService.findAll(_filter);
        setLicencas(result.items);

        setTotalRecords(result.totalRecords);
        if (resetPage) {
          setFirst(0);
        }
      } catch (err) {
        errorHandle(err);
      }
    },
    [calcLimit]
  );

  function onPage(event) {
    const pagina = event.first / event.rows;
    setPageSelected(pagina);
    setFirst(event.first);
    handleBuscar(filter, pagina, false);
  }

  async function gerarChave(numeroContrato) {
    setPedindoContrato(false);
    try {
      setChaveGerada(await LicencaService.gerarChave(numeroContrato));
      handleBuscar(filter);
    } catch (err) {
      errorHandle(err);
    }
  }

  function getTitle() {
    const titleDefault = 'Licenças (10.9+)';
    if (pageParams.stateScreen === StateScreen.stView && !isScreenMobile()) {
      return `${titleDefault} (Visualizando Id: ${pageParams.idSelected})`;
    }
    return titleDefault;
  }

  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      handleBuscar(filter, 0, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      calcLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcLimit]);

  return (
    <Container className="container-page page-enter-tab">
      <div className="p-grid">
        <CrudHeader
          title={getTitle()}
          showButtonFilter={pageParams.stateScreen === StateScreen.stSearch}
          showButtonInsert={false}
          showButtonSearch
          showButtonConfig={false}
          titleFilterDefault="Buscar por Contrato"
          handleFilterDefault={text => {
            filter.numeroContrato = text;
            setFilter({ ...filter, numeroContrato: text });
            handleBuscar(filter);
          }}
          handleClearFilters={() => {
            setFilter({
              ...filter,
              numeroContrato: '',
              situacao: '',
            });
          }}
          handleButtonSearch={() => handleBuscar(filter)}
        >
          <div className="p-col-12 p-sm-4 p-lg-3 p-fluid">
            <LabelSp>Situação</LabelSp>
            <DropdownSp
              options={OPCOES_SITUACAO}
              value={filter.situacao}
              onChange={e => setFilter({ ...filter, situacao: e.target.value })}
            />
          </div>
        </CrudHeader>
        {pageParams.stateScreen === StateScreen.stSearch ? renderSearch() : renderCrud()}
      </div>

      {pedindoContrato ? (
        <MessageInputText
          title="Gerar chave de instalação"
          caption="Número do contrato"
          messageVisible
          handleOk={gerarChave}
          handleClose={() => setPedindoContrato(false)}
        />
      ) : null}

      {chaveGerada ? (
        <DialogoChave resultado={chaveGerada} onClose={() => setChaveGerada(null)} />
      ) : null}
    </Container>
  );

  function renderSearch() {
    return (
      <>
        <div className="p-col-12" style={{ textAlign: 'start' }}>
          <ButtonSp
            className="p-button-success"
            icon="pi pi-key"
            label="Gerar chave de instalação"
            disabled={!AuthService.checkRoles('LICENCA_GERAR_CHAVE')}
            onClick={() => setPedindoContrato(true)}
          />
        </div>
        <div className="p-col-12 p-fluid datatable-doc-demo">
          <DataTableSp
            value={licencas}
            style={{ marginBottom: '2px' }}
            paginator
            rows={pageLimit}
            lazy
            responsive
            totalRecords={totalRecords}
            first={first}
            onPage={onPage}
          >
            <Column
              field="id"
              body={rowData => padLeft(rowData.id, 6)}
              header="Id"
              className="grid-col-id"
            />
            <Column
              field="cliente.numeroContrato"
              className="grid-col-curr"
              style={{ width: 100 }}
              header="Contrato"
              body={rowData => (rowData.cliente ? rowData.cliente.numeroContrato : '')}
            />
            <Column
              field="cliente.nome"
              className="grid-col"
              header="Cliente"
              body={rowData => (rowData.cliente ? rowData.cliente.nome : '')}
            />
            <Column
              field="situacao"
              className="grid-col"
              style={{ width: 160 }}
              header="Situação"
              body={rowData => NOMES_SITUACAO[rowData.situacao] || rowData.situacao}
            />
            <Column
              field="dataHoraEmissaoChave"
              className="grid-col"
              style={{ width: 160 }}
              header="Chave gerada em"
              body={rowData => formataDataHora(rowData.dataHoraEmissaoChave)}
            />
            <Column
              field="nomeMaquina"
              className="grid-col"
              style={{ width: 160 }}
              header="Servidor"
            />
            <Column
              field="dataHoraUltimaRenovacao"
              className="grid-col"
              style={{ width: 160 }}
              header="Última renovação"
              body={rowData => formataDataHora(rowData.dataHoraUltimaRenovacao)}
            />
            <Column
              className="gid-col-acoes-35"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonOp}
            />
          </DataTableSp>
        </div>
      </>
    );
  }

  function renderButtonOp(rowData) {
    return (
      <BotaoMenuGrid
        handles={[() => history.push(`/licencas/${rowData.id}?view`)]}
        labels={['Visualizar']}
        icons={['pi pi-eye']}
        disableds={[false]}
      />
    );
  }

  function renderCrud() {
    return (
      <LicencaCrud
        idSelected={pageParams.idSelected}
        onClose={() => {
          history.push(toBack);
          handleBuscar(filter, pageSelected, false);
        }}
      />
    );
  }
}
